import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { adminApi, publicApi } from "../../lib/api";
import { DAYS, defaultHours, SERVICES_PRESETS } from "../../lib/hours";
import { ArrowLeft, Loader2, Save, Upload, Trash2, MapPin, Plus, Send, Archive, Eye, Wand2, ChevronUp, ChevronDown, RefreshCw, AlertCircle, CheckCircle2 } from "lucide-react";
import SubcategorySelector from "../../components/taxonomy/SubcategorySelector";

// ---- Helpers -----------------------------------------------------------------
const URL_FIELDS = new Set([
  "website", "instagram_url", "facebook_url", "tiktok_url", "youtube_url",
  "linkedin_url", "booking_url", "ecommerce_url", "menu_url", "canonical_url",
]);

const slugify = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")   // rimuove diacritici
    .replace(/[^a-z0-9\s-]/g, "")       // rimuove caratteri speciali
    .trim()
    .replace(/\s+/g, "-")               // spazi -> trattini
    .replace(/-+/g, "-")                // collassa trattini
    .slice(0, 80);

const normalizeUrl = (url) => {
  const v = (url || "").trim();
  if (!v) return "";
  if (/^(https?:)?\/\//i.test(v)) return v.replace(/^\/\//, "https://");
  if (/^[a-z]+:/i.test(v)) return v; // mailto:, tel:, ecc.
  return `https://${v}`;
};

const EMPTY = {
  name: "", slug: "", category_slug: "", subcategory_slugs: [],
  short_description: "", full_description: "", slogan: "", year_founded: "",
  tags: [], services: [], products: [],
  address: "", latitude: "", longitude: "",
  phone: "", whatsapp: "", email: "", pec_email: "", website: "",
  instagram_url: "", facebook_url: "", tiktok_url: "", youtube_url: "", linkedin_url: "",
  booking_url: "", ecommerce_url: "", menu_url: "",
  contact_visibility: {},
  opening_hours: defaultHours(), opening_hours_notes: "",
  logo_url: "", cover_image_url: "", gallery: [], videos: [],
  seo_title: "", seo_description: "", seo_og_image: "", canonical_url: "",
  status: "draft", featured: false,
};

const Section = ({ title, subtitle, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="bg-white rounded-3xl border border-[#E5E7EB] overflow-hidden">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between px-6 md:px-8 py-5 text-left hover:bg-[#F8F9FA]">
        <div>
          <h2 className="font-display font-semibold text-lg text-[#1F2937]">{title}</h2>
          {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-6 md:px-8 pb-8">{children}</div>}
    </section>
  );
};

const Field = ({ label, hint, className = "", children }) => (
  <label className={`block ${className}`}>
    <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mb-2">{label}</div>
    {children}
    {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
  </label>
);

const Input = (props) => (
  <input {...props} className="w-full bg-[#F8F9FA] border border-[#E5E7EB] rounded-xl px-4 py-2.5 outline-none focus:border-[#12964A] focus:bg-white transition-colors" />
);

const Textarea = (props) => (
  <textarea {...props} className="w-full bg-[#F8F9FA] border border-[#E5E7EB] rounded-xl px-4 py-3 outline-none focus:border-[#12964A] focus:bg-white" />
);

const Select = (props) => (
  <select {...props} className="w-full bg-[#F8F9FA] border border-[#E5E7EB] rounded-xl px-4 py-2.5 outline-none focus:border-[#12964A]" />
);

const Chips = ({ options, value = [], onToggle }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => {
      const on = value.includes(o.slug || o);
      const key = o.slug || o;
      const label = o.name || o;
      return (
        <button type="button" key={key} onClick={() => onToggle(key)} className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? "bg-[#12964A] text-white border-[#12964A]" : "bg-white text-[#1F2937] border-[#E5E7EB] hover:border-[#12964A]"}`}>{label}</button>
      );
    })}
  </div>
);

const BusinessForm = () => {
  const { id } = useParams();
  const isNew = !id || id === "nuova";
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [allSubs, setAllSubs] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [msg, setMsg] = useState(null);
  const [tagInput, setTagInput] = useState("");
  const [srvInput, setSrvInput] = useState("");
  const [geocoding, setGeocoding] = useState(false);
  // Slug lock: quando l'utente edita a mano il campo slug, non lo rigeneriamo dal nome.
  // Usiamo sia un ref (per letture in callback) sia uno state (per reattività della UI hint).
  const slugTouchedRef = useRef(false);
  const [slugTouched, setSlugTouched] = useState(false);
  // Progress upload galleria multipla: {total, done, errors, items: [{name, status, error}]}
  const [galleryProgress, setGalleryProgress] = useState(null);

  const set = useCallback((k, v) => setF((s) => ({ ...s, [k]: v })), []);
  const setHour = (day, patch) => setF((s) => ({ ...s, opening_hours: { ...s.opening_hours, [day]: { ...s.opening_hours[day], ...patch } } }));

  // Aggiorna il nome e, se lo slug non è stato editato manualmente, lo rigenera
  const setName = useCallback((val) => {
    setF((s) => {
      const nextSlug = slugTouchedRef.current ? s.slug : slugify(val);
      return { ...s, name: val, slug: nextSlug };
    });
  }, []);

  // Riattiva la generazione automatica dello slug dal nome
  const regenerateSlug = useCallback(() => {
    slugTouchedRef.current = false;
    setSlugTouched(false);
    setF((s) => ({ ...s, slug: slugify(s.name) }));
  }, []);

  // onChange manuale sul campo slug: marca come "touched"
  const onSlugChange = useCallback((val) => {
    slugTouchedRef.current = true;
    setSlugTouched(true);
    setF((s) => ({ ...s, slug: val }));
  }, []);

  // Normalizza URL onBlur per un campo dato
  const normalizeFieldUrl = useCallback((k) => {
    setF((s) => {
      const current = s[k];
      const nv = normalizeUrl(current);
      if (nv === current) return s;
      return { ...s, [k]: nv };
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, s] = await Promise.all([publicApi.listCategories(), publicApi.listSubcategories()]);
        if (!cancelled) { setCategories(c.data || []); setAllSubs(s.data || []); }
      } catch (err) { console.warn(err); }
      if (!isNew) {
        try {
          const r = await adminApi.getBusiness(id);
          if (cancelled) return;
          const row = r.data;
          const subs = (row.business_subcategories || []).map((bs) => bs.subcategory?.slug).filter(Boolean);
          // Business esistente: consideriamo lo slug "toccato" (non lo rigeneriamo autom. cambiando nome)
          slugTouchedRef.current = Boolean(row.slug);
          setSlugTouched(Boolean(row.slug));
          setF({ ...EMPTY, ...row,
            slug: row.slug || "",
            category_slug: row.category?.slug || "",
            subcategory_slugs: subs,
            tags: row.tags || [], services: row.services || [], products: row.products || [],
            gallery: row.gallery || [], videos: row.videos || [],
            opening_hours: (row.opening_hours && Object.keys(row.opening_hours).length) ? row.opening_hours : defaultHours(),
            contact_visibility: row.contact_visibility || {},
            latitude: row.latitude ?? "", longitude: row.longitude ?? "",
            year_founded: row.year_founded ?? "",
          });
        } catch (e) { setMsg({ t: "error", m: "Impossibile caricare l'attività." }); }
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [id, isNew]);

  const availableSubs = useMemo(() => allSubs.filter((s) => s.category?.slug === f.category_slug), [allSubs, f.category_slug]);

  const toggleSub = (slug) => setF((s) => ({ ...s, subcategory_slugs: s.subcategory_slugs.includes(slug) ? s.subcategory_slugs.filter((x) => x !== slug) : [...s.subcategory_slugs, slug] }));
  const toggleSrv = (name) => setF((s) => ({ ...s, services: s.services.includes(name) ? s.services.filter((x) => x !== name) : [...s.services, name] }));
  const addTag = () => { if (tagInput.trim()) { set("tags", [...(f.tags || []), tagInput.trim()]); setTagInput(""); } };
  const removeTag = (t) => set("tags", (f.tags || []).filter((x) => x !== t));
  const addSrv = () => { if (srvInput.trim() && !f.services.includes(srvInput.trim())) { set("services", [...(f.services || []), srvInput.trim()]); setSrvInput(""); } };

  const upload = async (field, file) => {
    if (!file) return;
    setUploading(field);
    try {
      const r = await adminApi.upload(file, field === "logo" ? "logos" : field === "cover" ? "covers" : "gallery");
      const url = r.data.public_url;
      if (field === "logo") set("logo_url", url);
      else if (field === "cover") set("cover_image_url", url);
      else if (field === "og") set("seo_og_image", url);
      else if (field === "gallery") {
        // Functional update: legge lo stato più recente (fix closure stale su upload multipli)
        setF((s) => ({ ...s, gallery: [...(s.gallery || []), { url, caption: "", alt: "" }] }));
      }
    } catch (e) { setMsg({ t: "error", m: e?.response?.data?.detail || "Errore upload" }); }
    finally { setUploading(null); }
  };

  // Upload multipli con progress bar globale + stato per-thumbnail
  const uploadMultiple = async (files) => {
    if (!files || files.length === 0) return;
    // Snapshot iniziale dei metadati per la UI di progress
    const items = files.map((file) => ({
      name: file.name,
      size: file.size,
      preview: URL.createObjectURL(file),
      status: "queued", // queued | uploading | done | error
      error: null,
    }));
    setGalleryProgress({ total: files.length, done: 0, errors: 0, items });
    setMsg(null);

    // Upload sequenziale (backend supabase upload); usiamo setF functional per evitare closure stale
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setGalleryProgress((p) => {
        if (!p) return p;
        const next = { ...p, items: [...p.items] };
        next.items[i] = { ...next.items[i], status: "uploading" };
        return next;
      });
      try {
        const r = await adminApi.upload(file, "gallery");
        const url = r.data.public_url;
        setF((s) => ({ ...s, gallery: [...(s.gallery || []), { url, caption: "", alt: "" }] }));
        setGalleryProgress((p) => {
          if (!p) return p;
          const next = { ...p, items: [...p.items] };
          next.items[i] = { ...next.items[i], status: "done" };
          return { ...next, done: next.done + 1 };
        });
      } catch (e) {
        const errMsg = e?.response?.data?.detail || e?.message || "Errore upload";
        setGalleryProgress((p) => {
          if (!p) return p;
          const next = { ...p, items: [...p.items] };
          next.items[i] = { ...next.items[i], status: "error", error: errMsg };
          return { ...next, errors: next.errors + 1 };
        });
      }
    }

    // Messaggio finale
    setGalleryProgress((p) => {
      if (!p) return p;
      if (p.errors === 0) {
        setMsg({ t: "ok", m: `${p.done} immagini caricate con successo.` });
      } else if (p.done === 0) {
        setMsg({ t: "error", m: `Nessuna immagine caricata (${p.errors} errori).` });
      } else {
        setMsg({ t: "warn", m: `${p.done} caricate, ${p.errors} fallite. Le immagini riuscite sono state aggiunte alla galleria.` });
      }
      return p;
    });
  };

  // Chiude il pannello progress galleria (dopo che l'utente ha visto l'esito)
  const dismissGalleryProgress = () => {
    setGalleryProgress((p) => {
      if (p?.items) p.items.forEach((it) => it.preview && URL.revokeObjectURL(it.preview));
      return null;
    });
  };

  const moveGallery = (i, dir) => {
    const arr = [...(f.gallery || [])];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    set("gallery", arr);
  };
  const removeGallery = (i) => set("gallery", (f.gallery || []).filter((_, k) => k !== i));
  const updateGallery = (i, patch) => set("gallery", (f.gallery || []).map((x, k) => k === i ? { ...x, ...patch } : x));

  const addVideo = () => set("videos", [...(f.videos || []), { url: "", title: "", thumbnail: "" }]);
  const updateVideo = (i, patch) => set("videos", (f.videos || []).map((v, k) => k === i ? { ...v, ...patch } : v));
  const removeVideo = (i) => set("videos", (f.videos || []).filter((_, k) => k !== i));

  const doGeocode = async () => {
    if (!f.address || !f.address.trim()) return setMsg({ t: "error", m: "Compila prima l'indirizzo." });
    setGeocoding(true);
    setMsg(null);
    try {
      const r = await adminApi.geocode(f.address.trim());
      set("latitude", r.data.latitude); set("longitude", r.data.longitude);
      setMsg({ t: "ok", m: `Posizione trovata: ${r.data.display_name}` });
    } catch (e) { setMsg({ t: "error", m: e?.response?.data?.detail || "Geocodifica fallita. Prova ad aggiungere città e provincia." }); }
    finally { setGeocoding(false); }
  };

  const save = async (opts = {}) => {
    setSaving(true); setMsg(null);
    try {
      const payload = { ...f };
      // Normalizza tutti gli URL prima del salvataggio (safety net oltre all'onBlur)
      URL_FIELDS.forEach((k) => { if (payload[k]) payload[k] = normalizeUrl(payload[k]); });
      // Autogenera slug se vuoto e nome disponibile
      if (!payload.slug && payload.name) payload.slug = slugify(payload.name);
      payload.latitude = payload.latitude === "" ? null : parseFloat(payload.latitude);
      payload.longitude = payload.longitude === "" ? null : parseFloat(payload.longitude);
      payload.year_founded = payload.year_founded === "" ? null : parseInt(payload.year_founded, 10) || null;
      if (opts.status) payload.status = opts.status;
      if (isNew) {
        const r = await adminApi.createBusiness(payload);
        navigate(`/admin/attivita/${r.data.id}`);
      } else {
        await adminApi.updateBusiness(id, payload);
        setF((s) => ({ ...s, ...payload })); // riflette normalizzazione URL nel form
        setMsg({ t: "ok", m: "Salvato." });
      }
    } catch (e) { setMsg({ t: "error", m: e?.response?.data?.detail || "Errore salvataggio" }); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!window.confirm("Sei sicuro di voler eliminare questa attivit\u00e0? L'operazione \u00e8 irreversibile.")) return;
    try { await adminApi.deleteBusiness(id); navigate("/admin"); }
    catch (e) { setMsg({ t: "error", m: e?.response?.data?.detail || "Eliminazione fallita" }); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#12964A]" /></div>;

  return (
    <div className="min-h-screen bg-[#F8F9FA] pb-16">
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
          <Link to="/admin" className="inline-flex items-center gap-2 text-slate-600 hover:text-[#12964A] text-sm"><ArrowLeft className="w-4 h-4" /> Pannello</Link>
          <div className="flex items-center gap-2">
            {!isNew && f.slug && <a href={`/attivita/${f.slug}`} target="_blank" rel="noreferrer" className="hidden sm:inline-flex items-center gap-1.5 text-sm border border-[#E5E7EB] rounded-full px-3 py-1.5 hover:border-slate-400"><Eye className="w-3.5 h-3.5" /> Anteprima</a>}
            <button type="button" onClick={() => save({ status: "draft" })} disabled={saving} className="text-sm border border-[#E5E7EB] rounded-full px-4 py-2 hover:border-slate-400">Salva bozza</button>
            <button type="button" onClick={() => save({ status: "published" })} disabled={saving} className="inline-flex items-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] text-white rounded-full px-4 py-2 text-sm"><Send className="w-3.5 h-3.5" /> Pubblica</button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10 space-y-6">
        <div className="mb-2">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">{isNew ? "Nuova attivit\u00e0" : "Modifica attivit\u00e0"}</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-[#1F2937]">{f.name || "Senza nome"}</h1>
          {!isNew && (
            <div className="mt-2 inline-flex items-center gap-2 text-xs text-slate-500">
              Stato: <span className={`px-2 py-0.5 rounded-full text-[11px] uppercase tracking-wider ${f.status === "published" ? "bg-[#E8F5EC] text-[#0E7C3D]" : f.status === "review" ? "bg-[#FFF9C4] text-[#1F2937]" : "bg-slate-100 text-slate-600"}`}>{f.status}</span>
            </div>
          )}
        </div>

        {msg && (
          <div data-testid="business-form-msg" className={`text-sm rounded-xl px-4 py-3 ${msg.t === "ok" ? "bg-[#E8F5EC] text-[#0E7C3D] border border-[#12964A]/20" : msg.t === "warn" ? "bg-[#FFF9C4] text-[#7A5A00] border border-yellow-300" : "bg-red-50 text-red-700 border border-red-200"}`}>{msg.m}</div>
        )}

        <Section title="1. Informazioni principali">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Nome attività" className="md:col-span-2">
              <Input data-testid="business-name-input" required value={f.name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="Slug" hint={slugTouched ? "Modificato manualmente. Puoi rigenerarlo dal nome." : "Auto-generato dal nome. Modifica per bloccarlo."}>
              <div className="flex gap-2">
                <Input
                  data-testid="business-slug-input"
                  value={f.slug}
                  onChange={(e) => onSlugChange(e.target.value)}
                  placeholder="panificio-medau"
                />
                <button
                  type="button"
                  data-testid="business-slug-regenerate-btn"
                  onClick={regenerateSlug}
                  disabled={!f.name}
                  title="Rigenera dal nome"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#E5E7EB] hover:border-[#12964A] hover:bg-[#F0FAF3] text-sm text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Rigenera dal nome
                </button>
              </div>
            </Field>
            <Field label="Anno di apertura"><Input type="number" value={f.year_founded} onChange={(e) => set("year_founded", e.target.value)} placeholder="1985" /></Field>
            <Field label="Slogan / Claim" className="md:col-span-2"><Input value={f.slogan} onChange={(e) => set("slogan", e.target.value)} placeholder="Il pane come una volta" /></Field>
            <Field label="Tag di ricerca" className="md:col-span-2">
              <div className="flex gap-2 items-center">
                <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }} placeholder="Aggiungi tag e premi Invio" />
                <button type="button" onClick={addTag} className="px-3 py-2 rounded-xl border border-[#E5E7EB] hover:border-[#12964A]"><Plus className="w-4 h-4" /></button>
              </div>
              {f.tags.length > 0 && <div className="flex flex-wrap gap-2 mt-3">{f.tags.map((t) => <button type="button" key={t} onClick={() => removeTag(t)} className="inline-flex items-center gap-1.5 bg-[#F0FAF3] text-[#0E7C3D] px-3 py-1 rounded-full text-sm hover:bg-[#E8F5EC]">{t} <Trash2 className="w-3 h-3" /></button>)}</div>}
            </Field>
          </div>
        </Section>

        <Section title="2. Categoria e sottocategorie">
          <div className="space-y-5">
            <Field label="Categoria principale">
              <Select value={f.category_slug} onChange={(e) => { set("category_slug", e.target.value); set("subcategory_slugs", []); }}>
                <option value="">— Seleziona —</option>
                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </Select>
            </Field>
            {f.category_slug && (
              <Field label="Sottocategorie (multiple)" hint="Cerca per nome, alias o sinonimo. Se non trovi, crea una nuova sottocategoria.">
                <SubcategorySelector
                  value={f.subcategory_slugs}
                  onChange={(next) => set("subcategory_slugs", next)}
                  businessCtx={{ name: f.name, short_description: f.short_description, full_description: f.full_description, services: f.services, tags: f.tags }}
                  macroSlug={f.category_slug}
                />
              </Field>
            )}
          </div>
        </Section>

        <Section title="3. Descrizione e contenuti" subtitle="Supporta Markdown: **grassetto**, *corsivo*, - liste, [link](https://), # titoli">
          <div className="grid grid-cols-1 gap-5">
            <Field label="Descrizione breve" hint="1-2 righe usate nelle card e nei risultati di ricerca"><Textarea rows={2} value={f.short_description} onChange={(e) => set("short_description", e.target.value)} /></Field>
            <Field label="Descrizione completa" hint="Racconta storia, valori, prodotti e servizi"><Textarea rows={8} value={f.full_description} onChange={(e) => set("full_description", e.target.value)} placeholder={"# La nostra storia\n\nNel **1985** è nato il nostro laboratorio...\n\n- Pane artigianale\n- Pizza al taglio\n- Dolci sardi"} /></Field>
            <Field label="Prodotti principali (separati da virgola)"><Input value={(f.products || []).join(", ")} onChange={(e) => set("products", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} placeholder="Pane carasau, Coccoi, Dolci tipici" /></Field>
          </div>
        </Section>

        <Section title="4. Contatti" subtitle="Toggle la visibilità sul sito pubblico">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              ["phone", "Telefono"], ["whatsapp", "WhatsApp"], ["email", "Email"], ["pec_email", "PEC"],
              ["website", "Sito web"], ["instagram_url", "Instagram"], ["facebook_url", "Facebook"], ["tiktok_url", "TikTok"],
              ["youtube_url", "YouTube"], ["linkedin_url", "LinkedIn"], ["booking_url", "Prenotazioni"], ["ecommerce_url", "E-commerce"], ["menu_url", "Menu / Catalogo"],
            ].map(([k, label]) => {
              const isUrl = URL_FIELDS.has(k);
              return (
              <Field key={k} label={label}>
                <div className="flex items-center gap-2">
                  <Input
                    data-testid={`business-${k}-input`}
                    value={f[k] || ""}
                    onChange={(e) => set(k, e.target.value)}
                    onBlur={isUrl ? () => normalizeFieldUrl(k) : undefined}
                    placeholder={k.endsWith("_url") ? "https://" : ""}
                  />
                  <label className="inline-flex items-center gap-1 text-xs text-slate-500 whitespace-nowrap">
                    <input type="checkbox" checked={f.contact_visibility?.[k] !== false} onChange={(e) => set("contact_visibility", { ...f.contact_visibility, [k]: e.target.checked })} /> Pubblico
                  </label>
                </div>
              </Field>
            );})}
          </div>
        </Section>

        <Section title="5. Orari di apertura">
          <div className="space-y-3">
            {DAYS.map((d) => {
              const h = f.opening_hours[d.key] || {};
              return (
                <div key={d.key} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end p-4 bg-[#F8F9FA] rounded-xl">
                  <div className="md:col-span-2">
                    <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mb-1">{d.label}</div>
                    <Select value={h.mode || "closed"} onChange={(e) => setHour(d.key, { mode: e.target.value })}>
                      <option value="closed">Chiuso</option>
                      <option value="morning_afternoon">Mattina + Pomeriggio</option>
                      <option value="continuous">Aperto continuato</option>
                      <option value="24h">24 ore</option>
                      <option value="appointment">Su appuntamento</option>
                    </Select>
                  </div>
                  {(h.mode === "morning_afternoon" || h.mode === "continuous") && (
                    <>
                      <div><div className="text-[10px] uppercase text-slate-400 mb-1">Apre</div><Input type="time" value={h.am_open || ""} onChange={(e) => setHour(d.key, { am_open: e.target.value })} /></div>
                      <div><div className="text-[10px] uppercase text-slate-400 mb-1">Chiude</div><Input type="time" value={h.am_close || ""} onChange={(e) => setHour(d.key, { am_close: e.target.value })} /></div>
                      {h.mode === "morning_afternoon" && (<>
                        <div><div className="text-[10px] uppercase text-slate-400 mb-1">Riapre</div><Input type="time" value={h.pm_open || ""} onChange={(e) => setHour(d.key, { pm_open: e.target.value })} /></div>
                        <div><div className="text-[10px] uppercase text-slate-400 mb-1">Chiude</div><Input type="time" value={h.pm_close || ""} onChange={(e) => setHour(d.key, { pm_close: e.target.value })} /></div>
                      </>)}
                    </>
                  )}
                </div>
              );
            })}
            <Field label="Note orari (chiusure straordinarie, festività, ecc.)"><Textarea rows={2} value={f.opening_hours_notes || ""} onChange={(e) => set("opening_hours_notes", e.target.value)} /></Field>
          </div>
        </Section>

        <Section title="6. Servizi e caratteristiche">
          <Chips options={SERVICES_PRESETS} value={f.services} onToggle={toggleSrv} />
          <div className="flex gap-2 items-center mt-5">
            <Input value={srvInput} onChange={(e) => setSrvInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSrv(); } }} placeholder="Aggiungi servizio personalizzato" />
            <button type="button" onClick={addSrv} className="px-3 py-2 rounded-xl border border-[#E5E7EB] hover:border-[#12964A]"><Plus className="w-4 h-4" /></button>
          </div>
        </Section>

        <Section title="7. Posizione e mappa">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Indirizzo completo" className="md:col-span-2">
              <div className="flex gap-2">
                <Input data-testid="business-address-input" value={f.address} onChange={(e) => set("address", e.target.value)} placeholder="Via Carlo Felice 88, 09025 Sanluri (SU)" />
                <button
                  type="button"
                  data-testid="business-geocode-btn"
                  onClick={doGeocode}
                  disabled={geocoding || !f.address?.trim()}
                  className="inline-flex items-center gap-1.5 bg-[#F4E500] hover:bg-[#e5d700] text-[#1F2937] rounded-xl px-4 py-2.5 text-sm whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {geocoding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                  {geocoding ? "Ricerca..." : "Trova sulla mappa"}
                </button>
              </div>
            </Field>
            <Field label="Latitudine"><Input value={f.latitude} onChange={(e) => set("latitude", e.target.value)} placeholder="39.5666" /></Field>
            <Field label="Longitudine"><Input value={f.longitude} onChange={(e) => set("longitude", e.target.value)} placeholder="8.8964" /></Field>
            {f.latitude && f.longitude && (
              <div className="md:col-span-2 rounded-2xl overflow-hidden border border-[#E5E7EB] aspect-[16/7]">
                <iframe title="map" src={`https://www.openstreetmap.org/export/embed.html?bbox=${f.longitude-0.005}%2C${f.latitude-0.005}%2C${+f.longitude+0.005}%2C${+f.latitude+0.005}&layer=mapnik&marker=${f.latitude}%2C${f.longitude}`} className="w-full h-full" />
              </div>
            )}
          </div>
        </Section>

        <Section title="8. Foto e video">
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Logo">
                <MediaPicker value={f.logo_url} onChange={(v) => set("logo_url", v)} onUpload={(file) => upload("logo", file)} uploading={uploading === "logo"} />
              </Field>
              <Field label="Immagine di copertina">
                <MediaPicker value={f.cover_image_url} onChange={(v) => set("cover_image_url", v)} onUpload={(file) => upload("cover", file)} uploading={uploading === "cover"} />
              </Field>
            </div>
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-[0.15em] text-slate-500">Galleria fotografica</div>
                <label data-testid="business-gallery-upload-label" className="inline-flex items-center gap-2 text-sm text-[#12964A] cursor-pointer hover:underline">
                  <Upload className="w-4 h-4" />
                  {galleryProgress ? `${galleryProgress.done}/${galleryProgress.total} caricate` : "Aggiungi foto (multiple)"}
                  <input
                    data-testid="business-gallery-upload-input"
                    type="file"
                    multiple
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => { const files = Array.from(e.target.files || []); e.target.value = ""; uploadMultiple(files); }}
                  />
                </label>
              </div>
              {galleryProgress && (
                <div data-testid="business-gallery-progress" className="mb-4 rounded-2xl border border-[#E5E7EB] bg-white p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-medium text-[#1F2937]">
                      Caricamento galleria: <span data-testid="gallery-progress-counter">{galleryProgress.done}/{galleryProgress.total}</span>
                      {galleryProgress.errors > 0 && <span className="ml-2 text-red-600 text-xs">({galleryProgress.errors} errori)</span>}
                    </div>
                    {galleryProgress.done + galleryProgress.errors === galleryProgress.total && (
                      <button type="button" data-testid="gallery-progress-dismiss" onClick={dismissGalleryProgress} className="text-xs text-slate-500 hover:text-[#12964A]">Chiudi</button>
                    )}
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-3">
                    <div
                      className="h-full bg-[#12964A] transition-all"
                      style={{ width: `${Math.round(((galleryProgress.done + galleryProgress.errors) / galleryProgress.total) * 100)}%` }}
                    />
                  </div>
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                    {galleryProgress.items.map((it, idx) => (
                      <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border border-[#E5E7EB] bg-slate-50">
                        <img src={it.preview} alt="" className={`w-full h-full object-cover ${it.status === "done" ? "" : "opacity-50"}`} />
                        <div className="absolute inset-0 flex items-center justify-center">
                          {it.status === "uploading" && <Loader2 className="w-5 h-5 text-white animate-spin drop-shadow" />}
                          {it.status === "done" && <CheckCircle2 className="w-5 h-5 text-white bg-[#12964A] rounded-full drop-shadow" />}
                          {it.status === "error" && (
                            <div title={it.error} className="bg-red-600 rounded-full p-0.5"><AlertCircle className="w-4 h-4 text-white" /></div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {f.gallery.length === 0 && !galleryProgress && <p className="text-sm text-slate-400">Nessuna foto ancora.</p>}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {f.gallery.map((g, i) => (
                  <div key={g.url + i} className="border border-[#E5E7EB] rounded-2xl overflow-hidden bg-white">
                    <div className="aspect-[4/3] bg-slate-100"><img src={g.url} alt="" className="w-full h-full object-cover" /></div>
                    <div className="p-3 space-y-2">
                      <Input value={g.caption || ""} onChange={(e) => updateGallery(i, { caption: e.target.value })} placeholder="Didascalia" />
                      <Input value={g.alt || ""} onChange={(e) => updateGallery(i, { alt: e.target.value })} placeholder="Alt text (SEO)" />
                      <div className="flex items-center justify-between">
                        <div className="flex gap-1">
                          <button type="button" onClick={() => moveGallery(i, -1)} className="p-1.5 rounded border border-[#E5E7EB]"><ChevronUp className="w-3.5 h-3.5" /></button>
                          <button type="button" onClick={() => moveGallery(i, 1)} className="p-1.5 rounded border border-[#E5E7EB]"><ChevronDown className="w-3.5 h-3.5" /></button>
                        </div>
                        <button type="button" onClick={() => removeGallery(i)} className="text-xs text-red-600 hover:underline inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Rimuovi</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-[0.15em] text-slate-500">Video</div>
                <button type="button" onClick={addVideo} className="text-sm text-[#12964A] hover:underline inline-flex items-center gap-1.5"><Plus className="w-4 h-4" /> Aggiungi video</button>
              </div>
              {f.videos.length === 0 && <p className="text-sm text-slate-400">Nessun video ancora. Puoi incollare URL di YouTube o Vimeo.</p>}
              <div className="space-y-3">
                {f.videos.map((v, i) => (
                  <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end border border-[#E5E7EB] rounded-xl p-3">
                    <Field label="URL video" className="md:col-span-6"><Input value={v.url} onChange={(e) => updateVideo(i, { url: e.target.value })} placeholder="https://youtube.com/watch?v=..." /></Field>
                    <Field label="Titolo" className="md:col-span-5"><Input value={v.title} onChange={(e) => updateVideo(i, { title: e.target.value })} /></Field>
                    <div className="md:col-span-1 flex justify-end"><button type="button" onClick={() => removeVideo(i)} className="p-2 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>

        <Section title="9. SEO" defaultOpen={false}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Meta title" hint="~60 caratteri" className="md:col-span-2"><Input value={f.seo_title} onChange={(e) => set("seo_title", e.target.value)} /></Field>
            <Field label="Meta description" hint="~160 caratteri" className="md:col-span-2"><Textarea rows={2} value={f.seo_description} onChange={(e) => set("seo_description", e.target.value)} /></Field>
            <Field label="Open Graph image" className="md:col-span-2">
              <MediaPicker value={f.seo_og_image} onChange={(v) => set("seo_og_image", v)} onUpload={(file) => upload("og", file)} uploading={uploading === "og"} />
            </Field>
            <Field label="Canonical URL" className="md:col-span-2"><Input value={f.canonical_url} onChange={(e) => set("canonical_url", e.target.value)} placeholder="https://sanluriccn.it/attivita/..." /></Field>
          </div>
        </Section>

        <Section title="10. Pubblicazione">
          <div className="space-y-5">
            <Field label="Stato">
              <div className="flex flex-wrap gap-2">
                {[["draft","Bozza"],["review","In revisione"],["published","Pubblicata"],["archived","Archiviata"]].map(([v, l]) => (
                  <button type="button" key={v} onClick={() => set("status", v)} className={`px-4 py-2 rounded-full text-sm border transition-colors ${f.status === v ? "bg-[#12964A] text-white border-[#12964A]" : "bg-white text-[#1F2937] border-[#E5E7EB] hover:border-[#12964A]"}`}>{l}</button>
                ))}
              </div>
            </Field>
            {!isNew && (
              <div className="pt-6 border-t border-[#E5E7EB]">
                <button type="button" onClick={doDelete} className="inline-flex items-center gap-2 text-sm text-red-600 border border-red-200 rounded-full px-4 py-2 hover:bg-red-50"><Archive className="w-4 h-4" /> Elimina definitivamente</button>
              </div>
            )}
          </div>
        </Section>
      </main>
    </div>
  );
};

const MediaPicker = ({ value, onChange, onUpload, uploading }) => (
  <div className="flex items-start gap-3">
    {value ? (
      <div className="relative w-24 h-24 rounded-xl overflow-hidden bg-[#F0FAF3] border border-[#E5E7EB]">
        <img src={value} alt="" className="w-full h-full object-cover" />
        <button type="button" onClick={() => onChange("")} className="absolute top-1 right-1 bg-white/95 hover:bg-white rounded-full p-1"><Trash2 className="w-3.5 h-3.5 text-red-600" /></button>
      </div>
    ) : (
      <div className="w-24 h-24 rounded-xl bg-[#F0FAF3] border border-dashed border-[#E5E7EB] flex items-center justify-center text-slate-400"><Upload className="w-5 h-5" /></div>
    )}
    <div className="flex-1 space-y-2">
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="URL immagine" className="w-full bg-[#F8F9FA] border border-[#E5E7EB] rounded-xl px-4 py-2.5" />
      <label className="inline-flex items-center gap-2 text-sm text-[#12964A] cursor-pointer hover:underline">
        <Upload className="w-4 h-4" /> {uploading ? "Caricamento..." : "Carica file"}
        <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e.target.files?.[0])} />
      </label>
    </div>
  </div>
);

export default BusinessForm;
